import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useTranslation } from 'react-i18next';

dayjs.extend(isSameOrBefore);

function WeekView({ agendaItems, selectedDate, onItemClick, onEventClick, monthDayFormat = 'DD/MM' }) {
  const { t } = useTranslation();
  // Configuración de timeline
  const startHour = 8;
  const endHour = 20; // Última etiqueta visible; la ventana termina a las 21:00 para cubrir el último slot
  const slotMinutes = 15;
  const slotHeightPx = 40; // Altura visual de cada slot de 15 minutos
  const pixelsPerMinute = slotHeightPx / slotMinutes; // ~2.6667px por minuto

  // Obtener el rango de la semana (lunes a domingo)
  const getWeekRange = (date) => {
    const startOfWeek = dayjs(date).startOf('week').add(1, 'day'); // Lunes
    const endOfWeek = startOfWeek.add(6, 'day'); // Domingo
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }
    
    return { startOfWeek, endOfWeek, days };
  };

  const { startOfWeek, endOfWeek, days } = getWeekRange(selectedDate);

  // Procesar items de agenda para calcular end_time cuando falta pero hay service_duration
  const processedAgendaItems = (agendaItems || []).map(item => {
    if (!item.end_time && item.begin_time && item.product?.service_duration) {
      const beginTime = dayjs(item.begin_time);
      const calculatedEndTime = beginTime.add(item.product.service_duration, 'minute');
      return {
        ...item,
        end_time: calculatedEndTime.toISOString()
      };
    }
    return item;
  });

  // Agrupar items por día
  const itemsByDay = {};
  days.forEach(day => {
    const dayKey = day.format('YYYY-MM-DD');
    itemsByDay[dayKey] = processedAgendaItems.filter(item => {
      if (!item.begin_time) return false;
      const itemDate = dayjs(item.begin_time);
      return itemDate.format('YYYY-MM-DD') === dayKey;
    });
  });

  // Generar slots de tiempo únicamente para dibujar la grilla de fondo
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotMinutes) {
        const time = dayjs().hour(hour).minute(minute).second(0);
        slots.push({
          time: time,
          timeString: time.format('HH:mm')
        });
      }
    }
    return slots;
  };

  // Calcular duración de una tarea en minutos
  const calculateTaskDuration = (item) => {
    if (item.end_time) {
      const beginTime = dayjs(item.begin_time);
      const endTime = dayjs(item.end_time);
      return endTime.diff(beginTime, 'minute');
    } else if (item.product?.service_duration) {
      return item.product.service_duration;
    } else {
      return 15;
    }
  };

  // Detectar conflictos de horario entre tareas en un día específico
  const detectTimeConflicts = (items) => {
    const conflicts = [];
    
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const item1 = items[i];
        const item2 = items[j];
        
        if (!item1.begin_time || !item2.begin_time) continue;
        
        const start1 = dayjs(item1.begin_time);
        const duration1 = calculateTaskDuration(item1);
        const end1 = item1.end_time ? dayjs(item1.end_time) : start1.add(duration1, 'minute');
        
        const start2 = dayjs(item2.begin_time);
        const duration2 = calculateTaskDuration(item2);
        const end2 = item2.end_time ? dayjs(item2.end_time) : start2.add(duration2, 'minute');
        
        // Verificar si hay solapamiento
        if (start1.isBefore(end2) && start2.isBefore(end1)) {
          conflicts.push([i, j]);
        }
      }
    }
    
    return conflicts;
  };

  // Asignar columnas a tareas para evitar solapamiento visual en un día específico
  const assignColumns = (items) => {
    const conflicts = detectTimeConflicts(items);
    const itemColumns = new Array(items.length).fill(0);
    const maxColumns = new Array(items.length).fill(1);
    
    // Crear grupos de tareas conflictivas
    const conflictGroups = [];
    const processed = new Set();
    
    conflicts.forEach(([i, j]) => {
      if (!processed.has(i) && !processed.has(j)) {
        const group = new Set([i, j]);
        
        // Expandir el grupo con todas las tareas que tienen conflicto con cualquier miembro
        let changed = true;
        while (changed) {
          changed = false;
          conflicts.forEach(([a, b]) => {
            if (group.has(a) && !group.has(b)) {
              group.add(b);
              changed = true;
            } else if (group.has(b) && !group.has(a)) {
              group.add(a);
              changed = true;
            }
          });
        }
        
        conflictGroups.push(Array.from(group));
        group.forEach(idx => processed.add(idx));
      }
    });
    
    // Asignar columnas dentro de cada grupo
    conflictGroups.forEach(group => {
      const groupItems = group.map(idx => ({ ...items[idx], originalIndex: idx }));
      groupItems.sort((a, b) => dayjs(a.begin_time).diff(dayjs(b.begin_time)));
      
      const columns = [];
      
      groupItems.forEach(item => {
        const itemStart = dayjs(item.begin_time);
        const itemDuration = calculateTaskDuration(item);
        const itemEnd = item.end_time ? dayjs(item.end_time) : itemStart.add(itemDuration, 'minute');
        
        // Buscar la primera columna disponible
        let columnIndex = 0;
        while (columnIndex < columns.length) {
          const lastItemInColumn = columns[columnIndex];
          if (!lastItemInColumn) break;
          
          const lastEnd = lastItemInColumn.end_time ? 
            dayjs(lastItemInColumn.end_time) : 
            dayjs(lastItemInColumn.begin_time).add(calculateTaskDuration(lastItemInColumn), 'minute');
          
          if (lastEnd.isSameOrBefore(itemStart)) {
            break; // Esta columna está disponible
          }
          columnIndex++;
        }
        
        // Asignar a la columna encontrada o crear nueva
        columns[columnIndex] = item;
        itemColumns[item.originalIndex] = columnIndex;
        maxColumns[item.originalIndex] = Math.max(columnIndex + 1, columns.length);
      });
      
      // Actualizar maxColumns para todo el grupo
      const groupMaxColumns = Math.max(...group.map(idx => maxColumns[idx]));
      group.forEach(idx => {
        maxColumns[idx] = groupMaxColumns;
      });
    });
    
    return { itemColumns, maxColumns };
  };

  // Grilla de slots para el fondo
  const timeSlots = generateTimeSlots();

  // Calcular posición y altura visual de cada tarea para la capa overlay
  const computeTaskBlock = (item, index, dayStart, dayEnd) => {
    if (!item.begin_time) {
      return { topPx: 0, heightPx: slotHeightPx, leftPx: 0, widthPercent: 100 };
    }
    const begin = dayjs(item.begin_time);
    const rawDuration = calculateTaskDuration(item);
    const end = item.end_time ? dayjs(item.end_time) : begin.add(rawDuration, 'minute');

    const clampedStart = begin.isBefore(dayStart) ? dayStart : begin;
    const clampedEnd = end.isAfter(dayEnd) ? dayEnd : end;

    const startMinutes = Math.max(0, clampedStart.diff(dayStart, 'minute'));
    const durationMinutes = Math.max(0, clampedEnd.diff(clampedStart, 'minute'));

    const topPx = startMinutes * pixelsPerMinute;
    const heightPx = Math.max(durationMinutes * pixelsPerMinute, 10);

    return { 
      topPx, 
      heightPx, 
      durationMinutes
    };
  };

  // Paleta de colores para diferentes usuarios
  const userColors = [
    'primary',    // Azul
    'secondary',  // Púrpura
    'error',      // Rojo
    'warning',    // Amarillo/Naranja
    'info',       // Cyan
    'success',    // Verde
  ];

  const getItemColor = (item) => {
    // Asignar color basado en el ID del usuario
    if (item.user?.id) {
      const colorIndex = item.user.id % userColors.length;
      return userColors[colorIndex];
    }
    
    return 'primary'; // Color por defecto
  };

  const getChipProps = (item) => {
    if (item.executed === 1) {
      return {
        label: t('agenda.completed'),
        color: 'success'
      };
    } else {
      return {
        label: t('agenda.pending'),
        color: 'warning'
      };
    }
  };

  const handleEventClick = (event, item) => {
    event.stopPropagation();
    onEventClick(event, item);
  };

  // Obtener usuarios únicos para la leyenda
  const uniqueUsers = processedAgendaItems.reduce((users, item) => {
    if (item.user && !users.find(u => u.id === item.user.id)) {
      users.push(item.user);
    }
    return users;
  }, []);

  const dayNames = [t('agenda.monday'), t('agenda.tuesday'), t('agenda.wednesday'), t('agenda.thursday'), t('agenda.friday'), t('agenda.saturday'), t('agenda.sunday')];

  return (
    <Box>
      {/* Leyenda de usuarios */}
      {uniqueUsers.length > 1 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('agenda.users')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {uniqueUsers.map(user => {
              const colorIndex = user.id % userColors.length;
              const color = userColors[colorIndex];
              return (
                <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: `${color}.main`,
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {user.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      
      {/* Vista de calendario semanal */}
      <Box sx={{ height: '600px', overflowY: 'auto' }}>
        {/* Contenedor relativo para permitir overlay absoluto */}
        <Box sx={{ position: 'relative' }}>
          {/* Encabezados de días */}
          <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper' }}>
            {/* Espacio para la columna de tiempo */}
            <Box sx={{ width: '80px', flexShrink: 0, borderRight: '1px solid', borderColor: 'divider' }} />
            
            {/* Encabezados de días */}
            {days.map((day, index) => (
              <Box
                key={day.format('YYYY-MM-DD')}
                sx={{
                  flex: 1,
                  p: 1,
                  borderRight: index < 6 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  borderBottom: '2px solid',
                  borderBottomColor: 'divider',
                  bgcolor: 'grey.50',
                  textAlign: 'center'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {dayNames[index]}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {day.format(monthDayFormat)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Capa de tareas superpuesta sobre la grilla */}
          {days.map((day, dayIndex) => {
            const dayKey = day.format('YYYY-MM-DD');
            const dayItems = itemsByDay[dayKey] || [];
            const dayStart = day.hour(startHour).minute(0).second(0).millisecond(0);
            const dayEnd = day.hour(endHour + 1).minute(0).second(0).millisecond(0);
            
            // Calcular distribución de columnas para las tareas del día
            const { itemColumns, maxColumns } = assignColumns(dayItems);

            return (
              <Box
                key={dayKey}
                sx={{
                  position: 'absolute',
                  top: '60px', // Altura del encabezado
                  left: `calc(80px + (${dayIndex} / 7) * (100% - 80px))`,
                  width: `calc((100% - 80px) / 7 - 1px)`, // Menos 1px para evitar solapamiento con bordes
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              >
                {dayItems.map((item, index) => {
                  const { topPx, heightPx, durationMinutes } = computeTaskBlock(item, index, dayStart, dayEnd);
                  const color = getItemColor(item);
                  const chipProps = getChipProps(item);
                  
                  // Calcular posición horizontal basada en columnas
                  const columnIndex = itemColumns[index];
                  const totalColumns = maxColumns[index];
                  const widthPercent = 100 / totalColumns;
                  const leftPercent = (columnIndex * widthPercent);
                  
                  // Usar una lógica de padding similar a DayView para dividir el espacio
                  // const horizontalPadding = totalColumns > 1 ? 2 : 2;
                  const horizontalPadding = 2;
                  
                  return (
                    <Paper
                      key={`${item.id || 'item'}-${index}`}
                      elevation={2}
                      onClick={(e) => handleEventClick(e, item)}
                      sx={{
                        position: 'absolute',
                        top: `${topPx}px`,
                        left: `calc(${leftPercent}% + ${horizontalPadding}px)`,
                        width: `calc(${widthPercent}% - ${horizontalPadding * 2}px)`,
                        height: `${heightPx}px`,
                        boxSizing: 'border-box',
                        p: totalColumns > 2 ? 0.25 : 0.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: `${color}.main`,
                        borderLeft: '3px solid',
                        borderLeftColor: `${color}.main`,
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Contenedor con posición relativa para el chip absoluto */}
                      <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {/* Primera fila: Hora */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.65rem',
                            lineHeight: 1.1
                          }}
                        >
                          {dayjs(item.begin_time).format('HH:mm')}
                          {item.end_time && ` - ${dayjs(item.end_time).format('HH:mm')}`}
                        </Typography>
                        
                        {/* Segunda fila: Usuario y evento */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 'medium',
                            fontSize: '0.6rem',
                            lineHeight: 1.1,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.user?.name && `${item.user.name}`}
                          {item.user?.name && item.event_name && ' • '}
                          {item.event_name}
                        </Typography>

                        {/* Chip de estado posicionado en la esquina inferior derecha */}
                        {heightPx > 30 && (
                          <Box sx={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            right: 0 
                          }}>
                            <Chip
                              label={chipProps.label}
                              size="small"
                              color={chipProps.color}
                              variant="outlined"
                              sx={{
                                fontSize: '0.55rem',
                                height: '14px',
                                '& .MuiChip-label': {
                                  px: 0.5
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            );
          })}

          {/* Grilla de fondo con columnas de tiempo y días */}
          {timeSlots.map((slot, slotIndex) => (
            <Box
              key={`${slot.timeString}-${slotIndex}`}
              sx={{
                display: 'flex',
                minHeight: `${slotHeightPx}px`,
                borderBottom: '1px solid',
                borderColor: 'divider',
                marginTop: 0, // Sin espacio adicional entre encabezados y primera fila
              }}
            >
              {/* Columna de tiempo */}
              <Box
                sx={{
                  width: '80px',
                  flexShrink: 0,
                  p: 1,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'flex-start',
                  bgcolor: slot.time.minute() === 0 ? 'grey.50' : 'transparent',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    fontWeight: slot.time.minute() === 0 ? 'bold' : 'normal',
                  }}
                >
                  {slot.timeString}
                </Typography>
              </Box>

              {/* Columnas de días */}
              {days.map((day, dayIndex) => (
                <Box
                  key={`${day.format('YYYY-MM-DD')}-${slotIndex}`}
                  sx={{
                    flex: 1,
                    borderRight: dayIndex < 6 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default WeekView;
